import { Toaster as Sonner } from 'sonner';
import { useState, useEffect } from 'preact/hooks';
import { getCanvasScale, onCanvasScaleChange } from '../../utils/uiScale';
import {
  GOLD, ACCENT_GOLD, PARCHMENT, PANEL_BG, CONTENT_BG_DEFAULT, TEXT_PRIMARY,
} from '../../lib/medievalTheme';
import HeraldSeal from './HeraldSeal';

// Sonner toasts restyled to the herald banner language introduced by
// the achievement unlock toast: textured stone surface, gold inset
// rim + glow, a wax-seal medallion hanging off the left edge, and a
// single foil-sheen pass after the toast lands. Injected as a global
// stylesheet because Sonner owns the toast DOM. We can only reach
// its [data-*] hooks from CSS.
//
// Injected on module load rather than app.css because Bun's CSS
// bundler tries to resolve url(...) at build time; going through
// document.head lets us keep everything in JS. Canvas scale reaches
// the sheet through the --vk-scale custom property set on the
// Toaster container (toasts portal to document.body, escaping the
// .app-canvas cqw context).
if (typeof document !== 'undefined' && !document.getElementById('valkenhall-toast-herald')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'valkenhall-toast-herald';
  styleEl.textContent = `
    [data-sonner-toast] {
      isolation: isolate;
    }
    /* Wax-seal medallion overhangs the card's left edge. The herald
       banner's signature silhouette. Sonner renders the icon inline;
       we pull it out of flow and let the padding rule below clear it. */
    /* !important throughout: sonner injects its own [data-icon]
       geometry (16px box, margins) at first mount. Later in the
       cascade than this module-load sheet, same specificity. */
    [data-sonner-toast] [data-icon] {
      position: absolute !important;
      left: calc(-13px * var(--vk-scale, 1)) !important;
      top: 50% !important;
      transform: translateY(-50%) !important;
      margin: 0 !important;
      width: auto !important;
      height: auto !important;
    }
    [data-sonner-toast]:has([data-icon]) {
      padding-left: calc(34px * var(--vk-scale, 1)) !important;
    }
    [data-sonner-toast] [data-title] {
      font-weight: 600;
      letter-spacing: 0.01em;
    }
    /* One foil-sheen pass once the toast has landed. background-position
       animation on a pseudo-element needs no overflow clipping. */
    @keyframes vk-toast-sheen {
      from { background-position: 200% 0; }
      to { background-position: -100% 0; }
    }
    [data-sonner-toast][data-mounted="true"]::before {
      content: "";
      position: absolute;
      inset: 0;
      border-radius: inherit;
      pointer-events: none;
      z-index: 1;
      background: linear-gradient(105deg, transparent 40%, rgba(255, 230, 160, 0.13) 50%, transparent 60%);
      background-size: 250% 100%;
      background-repeat: no-repeat;
      mix-blend-mode: screen;
      animation: vk-toast-sheen 0.6s ease-in-out 0.45s both;
    }
    @media (prefers-reduced-motion: reduce) {
      [data-sonner-toast][data-mounted="true"]::before { animation: none; opacity: 0; }
    }
  `;
  document.head.appendChild(styleEl);
}

// Minimal stroke glyphs for the seal interior. icons.jsx has no
// status glyphs (checked), so these stay private to the toaster.
function SealGlyph({ paths, color, size }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {paths.map((d) => <path key={d} d={d} />)}
    </svg>
  );
}

const SEAL_GLYPHS = {
  success: { paths: ['M4 12l5 5L20 7'], color: '#e8c466', glow: `${GOLD} 0.4)` },
  error: { paths: ['M6 6l12 12', 'M18 6L6 18'], color: '#d87a7a', glow: 'rgba(216, 122, 122, 0.35)' },
  warning: { paths: ['M12 5v9', 'M12 18h.01'], color: '#e0a94f', glow: 'rgba(224, 169, 79, 0.35)' },
  info: { paths: ['M12 10v9', 'M12 6h.01'], color: `${PARCHMENT} 0.9)`, glow: `${GOLD} 0.3)` },
};

function sealIcon(type, sealSize, glyphSize) {
  const { paths, color, glow } = SEAL_GLYPHS[type];
  return (
    <HeraldSeal size={`${sealSize}px`} glowColor={glow}>
      <SealGlyph paths={paths} color={color} size={glyphSize} />
    </HeraldSeal>
  );
}

function Toaster(props) {
  // Toasts portal to document.body, escaping the .app-canvas cqw context, so
  // we have to track canvas scale manually and apply it as plain pixel values.
  const [scale, setScale] = useState(getCanvasScale());
  useEffect(() => onCanvasScaleChange(setScale), []);
  const width = Math.round(356 * scale);
  const fontSize = Math.round(14 * scale);
  const descFontSize = Math.round(12 * scale);
  const padding = Math.round(16 * scale);
  const btnPadX = Math.round(12 * scale);
  const btnPadY = Math.round(6 * scale);
  const btnFont = Math.round(12 * scale);
  const gap = Math.round(8 * scale);
  const toastRadius = Math.round(8 * scale);
  const btnRadius = Math.round(6 * scale);
  const sealSize = Math.round(34 * scale);
  const glyphSize = Math.round(15 * scale);

  return (
    <Sonner
      theme="dark"
      position="top-center"
      className="toaster group"
      visibleToasts={5}
      gap={gap}
      icons={{
        success: sealIcon('success', sealSize, glyphSize),
        error: sealIcon('error', sealSize, glyphSize),
        warning: sealIcon('warning', sealSize, glyphSize),
        info: sealIcon('info', sealSize, glyphSize),
      }}
      style={{
        '--vk-scale': scale,
        '--normal-bg': PANEL_BG,
        '--normal-text': TEXT_PRIMARY,
        '--normal-border': 'transparent',
        '--border-radius': `${toastRadius}px`,
        '--width': `${width}px`,
      }}
      toastOptions={{
        style: {
          // Herald card surface: textured stone wash over the dark
          // panel base, gold inset rim + glow carried by the shadow
          // stack instead of a flat border.
          background: `${CONTENT_BG_DEFAULT}, ${PANEL_BG}`,
          backgroundBlendMode: 'overlay, normal',
          border: 'none',
          borderRadius: `${toastRadius}px`,
          boxShadow: [
            '0 16px 48px rgba(0,0,0,0.6)',
            `0 0 24px ${GOLD} 0.18)`,
            `inset 0 0 0 1px ${GOLD} 0.28)`,
          ].join(', '),
          color: TEXT_PRIMARY,
          fontSize: `${fontSize}px`,
          padding: `${padding}px`,
          width: `${width}px`,
        },
        descriptionStyle: {
          color: 'rgba(166, 160, 155, 0.5)',
          fontSize: `${descFontSize}px`,
        },
        actionButtonStyle: {
          background: 'linear-gradient(180deg, rgba(212,168,67,0.9) 0%, rgba(160,120,40,0.9) 100%)',
          border: `1px solid ${ACCENT_GOLD}99`,
          color: '#1a1408',
          borderRadius: `${btnRadius}px`,
          fontSize: `${btnFont}px`,
          padding: `${btnPadY}px ${btnPadX}px`,
        },
        cancelButtonStyle: {
          background: 'linear-gradient(180deg, rgba(255,255,255,0.07) 0%, rgba(0,0,0,0.12) 100%)',
          border: `1px solid ${GOLD} 0.3)`,
          color: '#A6A09B',
          borderRadius: `${btnRadius}px`,
          fontSize: `${btnFont}px`,
          padding: `${btnPadY}px ${btnPadX}px`,
        },
      }}
      {...props}
    />
  );
}

export { Toaster };
